<template>
    <div v-if="isShowLoading" class="modal">
        <div class="page-spinner-wrapper">
            <span class="spinner-circle"></span>
        </div>
    </div>
    <div
        v-else
        id="login"
        class="login"
        :class="{ 'hide-social': hideSocialOptions, 'parental-flow': parentalFlowMode }"
    >
        <FormError data-cy="page-error" :data-cy-data="errorCode" :hide-error="!errorCode" :error-code="errorCode">
            <template v-if="errorActionMessage">
                <span
                    class="error-form-action"
                    :class="{ clickable: errorAction }"
                    @click="errorAction ? errorAction() : null"
                    >{{ errorActionMessage }}</span
                >
            </template>
        </FormError>

        <accountContainer :class="{ narrow: parentalFlowMode }" :sub-title-text="$t(title)">
            <column class="pull-left">
                <parental-login-guide v-if="parentalFlowMode" :mode="parentalFlowMode" />
                <form
                    class="input-container"
                    novalidate
                    @submit.prevent="() => (showPassword ? loginHandler() : continueHandler())"
                >
                    <h5 id="page-main-heading">{{ $t(subTitle) }}</h5>
                    <regionSelect />
                    <FormInput
                        :id="'id'"
                        v-model="$v.inputAccountId.$model"
                        data-cy="account-id"
                        :type="idPlaceHolder.inputType"
                        :override-error-text="$t(inputAccountIdErrorText, [3, 50])"
                        :has-error="submitted && ($v.inputAccountId.$error || showInvalidEmailError)"
                        :placeholder="idPlaceHolder.placeholder"
                        :autocomplete="'email'"
                    >
                    </FormInput>
                    <transition name="fade">
                        <FormPassword
                            v-if="showPassword"
                            :id="'password'"
                            v-model="$v.inputPassword.$model"
                            data-cy="password"
                            type="password"
                            :max-length="32"
                            :has-error="submitted && $v.inputPassword.$error"
                            :override-error-text="$t('error.input.password')"
                            :enter-handler="loginHandler"
                            :placeholder="$t('login.password')"
                        >
                        </FormPassword>
                    </transition>
                    <FormCheckbox
                        id="auto-login"
                        :checked="isLauncherMode() ? autologin : autoLoginWeb"
                        name="auto-login"
                        :label-text="$t('login.auto-login')"
                        @change="
                            (val) =>
                                isLauncherMode()
                                    ? $store.commit('login/launcher/setAutologin', val)
                                    : $store.commit('login/setAutoLoginWeb', val)
                        "
                    >
                    </FormCheckbox>
                    <reCaptcha
                        v-if="showCaptcha"
                        v-model="$v.reCaptchaCode.$model"
                        :has-error="submitted && $v.reCaptchaCode.$error"
                        :override-error-text="$t('error.form.complete-captcha')"
                        @captcha-completed="setCaptchaCode"
                    >
                    </reCaptcha>

                    <transition name="fade">
                        <termsLinks v-if="showPassword" :action="$t('login.login')" />
                    </transition>

                    <FormButton
                        id="submitLogin"
                        data-cy="submit"
                        class="btn-gradient"
                        type="submit"
                        :loading="buttonLoading"
                        :label="$t(`${showPassword ? 'login.login' : 'create-account.complete.continue'}`)"
                    >
                    </FormButton>
                </form>

                <ul class="help list-unstyled">
                    <li class="trouble-logging-in">
                        <router-link
                            data-cy="link-forgot-password"
                            :to="{ name: 'self-service' }"
                            @click.native="trackTroubleLogging"
                        >
                            <svgIcon icon-holder-class="icon-help" icon-id="icon-page-right" />{{
                                $t('login.help.trouble-logging-in')
                            }}</router-link
                        >
                    </li>
                    <li>
                        <router-link id="create-account-link" :to="{ name: 'create-account' }">
                            <svgIcon icon-holder-class="icon-help" icon-id="icon-page-right" />
                            {{ $t('login.help.create-account') }}
                        </router-link>
                    </li>
                </ul>

                <recaptcha-attribution />
            </column>

            <column v-if="!hideSocialOptions" class="pull-right">
                <div class="divider">or</div>
                <thirdParty
                    v-if="!hasFromOPENAPI_WEBSITE"
                    tpa-type="loginTPA"
                    :third-party-header="$t('login.login-with')"
                    :oauth-state="{
                        ...(parentalFlowMode
                            ? {
                                  parental_flow_mode: parentalFlowMode,
                                  route_from: $route.name,
                              }
                            : {}),
                    }"
                >
                </thirdParty>
                <recaptcha-attribution />
            </column>

            <div class="clearfix"></div>

            <div v-if="productId" class="product-logo" :class="`product-${productId}`"></div>
        </accountContainer>
        <keep-progress :is-initial-show-modal="checkIsShowModal" />
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { FormButton, FormCheckbox, FormError, FormInput, FormPassword } from '@arena/toolkit';
import { email, helpers, maxLength, required } from 'vuelidate/lib/validators';
import svgIcon from '@arena/toolkit/src/components/common/icon-svg';
import { waitForCookieSet } from '@/plugins/helpers';
import accountContainer from '@/components/account-container';
import column from '@/components/column-container';
import thirdParty from '@/components/third-party';
import termsLinks from '@/components/terms-links';
import reCaptcha from '@/components/recaptcha';
import regionSelect from '@/components/region-select';
import launcherAPI from '@/modules/launcher';
import recaptchaAttribution from '@/components/recaptcha-attribution';
import domains from '@/config/domains';
import parentalLoginGuide from '@/components/parental-controls/login-guide.vue';
import KeepProgress from '@/components/dialogs/keep-progress';

export default {
    name: 'Login',
    components: {
        accountContainer,
        FormCheckbox,
        FormInput,
        FormButton,
        FormError,
        FormPassword,
        thirdParty,
        column,
        reCaptcha,
        regionSelect,
        svgIcon,
        termsLinks,
        recaptchaAttribution,
        parentalLoginGuide,
        KeepProgress,
    },
    metaInfo() {
        return {
            titleTemplate: `%s | ${this.$t('login.login')}`,
        };
    },
    props: {
        isUnified: {
            type: Boolean,
            default: false,
        },
        needCheckMinorLinking: {
            type: Boolean,
            default: false,
        },
        minorPlatforms: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            inputAccountId: '',
            // TODO: REMOVE THIS PLEASE!
            inputPassword: '',
            reCaptchaCode: '',
            buttonLoading: false,
            submitted: false,
            errorCode: 0,
            modalErrorCode: null,
            showCaptcha: false,
            errorActionMessage: '',
            errorAction: null,
            switchToRegion: '',
            showPassword: false,
            dialogOpen: false,
            showInvalidEmailError: false,
            isShowLoading: false,
        };
    },
    validations() {
        return {
            inputAccountId: {
                required,
                maxLength: maxLength(50),
                ...(this.isLauncherMode() ? { email } : {}),
            },
            inputPassword: { required },
            reCaptchaCode: {
                reCaptchaRequireCheck: (value) => helpers.req(value) || !this.showCaptcha,
            },
        };
    },
    computed: {
        ...mapState('login/launcher', ['autologin']),
        ...mapState('login', ['autoLoginWeb']),
        ...mapState('country', ['ipMembershipNo']),
        hasFromOPENAPI_WEBSITE() {
            if (typeof window.sessionStorage !== 'undefined') {
                return !!window.sessionStorage.getItem('from_openapi_website');
            }
            return false;
        },
        hideSocialOptions() {
            return environment.subRegion === 'ru';
        },
        checkIsShowModal() {
            return false;
            // only for KD - REMOVED CROSS PLATFORM Playing banner at 2024-09-27
            // return this.$route.query.prod_id === '50203' && !this.isSMSOptInFlow;
        },
        inputAccountIdErrorText() {
            if (this.showInvalidEmailError) {
                return 'error.input.not-valid-email';
            }
            if (this.isLauncherMode()) {
                if (!this.$v.inputAccountId.required) {
                    return 'error.input.email';
                }
                if (!this.$v.inputAccountId.maxLength) {
                    return 'error.input.length.id';
                }
                return 'error.input.not-valid-email';
            }
            if (!this.$v.inputAccountId.maxLength) {
                return 'error.input.length.id';
            }
            return 'error.input.id';
        },
        idPlaceHolder() {
            if (this.isLauncherMode()) {
                return {
                    placeholder: this.$t('login.email'),
                    inputType: 'email',
                };
            }
            return {
                placeholder: this.$t('login.id'),
                inputType: 'email',
            };
        },
        productId() {
            return this.$cookie.get('ProdId');
        },
        modalSubmitMode() {
            const option = this.modalErrorCode;

            if (option === 20037) {
                return 'request-unprotect-s';
            }
            if (option === 20102) {
                return 'blocked-user-suspicious-ip';
            }
            return 'request-unprotect-n';
        },
        getCurrentTime() {
            return Date.now();
        },
        parentalFlowMode() {
            switch (this.$route.name) {
                case 'add-child-login':
                    return 'child';
                case 'parent-login':
                    return 'parent';
                default:
                    return undefined;
            }
        },
        title() {
            if (this.parentalFlowMode === 'parent') return 'parental-controls.parent.login.title';
            if (this.parentalFlowMode === 'child') return 'parental-controls.child.login.title';
            return '';
        },
        subTitle() {
            if (this.parentalFlowMode === 'child') return 'parental-controls.child.login.login-to-nexon';
            return 'login.login-to-nexon';
        },
        isSMSOptInFlow() {
            return this.$route.query.smsoptin === 'true';
        },
    },
    watch: {
        errorCode(val) {
            if (val) {
                window.scrollTo(0, 0); // for top error bar's visibility
            }
        },
        '$route.query.errorCode'(newVal, oldVal) {
            if (newVal !== oldVal) {
                // handle the route query parameter change here
                this.errorCode = newVal;
            }
        },
    },
    mounted() {
        if (this.$route.query.errorCode) {
            this.errorCode = this.$route.query.errorCode;
        }
        if (this.$route.params.email && this.$route.params.newPassword) {
            this.isShowLoading = true;
            this.inputAccountId = this.$route.params.email;
            this.inputPassword = this.$route.params.newPassword;
            this.loginHandler();
        }
        if (this.$route.params.errorObj) {
            this.handleLoginFailure(this.$route.params.errorObj);
        }
        const elInputId = document.querySelector('input#id');
        if (elInputId) elInputId.focus();

        if (this.isUnified && !this.$route.query.unified) {
            this.$router.replace({
                query: {
                    ...this.$route.query,
                    unified: 'unified',
                },
            });
        }

        const queryParentalMode = this.$route.query.parental_flow_mode;
        if (this.parentalFlowMode !== queryParentalMode) {
            if (!queryParentalMode) {
                this.$router.replace({
                    query: {
                        ...this.$route.query,
                        ...(this.parentalFlowMode ? { parental_flow_mode: this.parentalFlowMode } : {}),
                    },
                });
            } else {
                this.$router.replace({
                    name: queryParentalMode === 'parent' ? 'parent-login' : 'add-child-login',
                    query: this.$route.query,
                });
            }
        }

        if (this.isSMSOptInFlow) {
            this.$dialog.smsOptInLoginCreate().then((haveAccount) => {
                if (!haveAccount) {
                    this.$router.replace({
                        name: 'create-account',
                        query: {
                            ...this.$route.query,
                        },
                    });
                } else {
                    if (elInputId) elInputId.focus();
                }
            });
        }

        if (this.needCheckMinorLinking) {
            this.openMinorLinkingModal(this.minorPlatforms);
        }
    },
    async created() {
        // for nxLog Funner tracking backend need a data when page initiated
        await waitForCookieSet('arenaSid');
        this.isUnified
            ? await this.$regionalAPI.post('/no-auth/unified/login/init')
            : await this.$accountAPI.post('/no-auth/login/init');
    },
    methods: {
        ...mapActions('login', ['login', 'emailCheck']),
        isLauncherMode: launcherAPI.isAppMode,
        async continueHandler() {
            this.showInvalidEmailError = false;
            this.inputAccountId = this.inputAccountId.trim(); // remove white space from input account id
            this.submitted = true;
            this.$v.inputAccountId.$touch();
            if (this.$v.inputAccountId.$invalid || this.dialogOpen) return;
            this.errorActionMessage = '';
            this.errorCode = 0;
            dataLayer.push({
                event: 'AccountLoginContinue',
                tpaType: 'Nexon',
            });
            try {
                await this.emailCheck({ id: this.inputAccountId });
                this.showPasswordField();
            } catch (err) {
                const { errorCode } = err;
                if (errorCode === 20002) {
                    if (this.inputAccountId.indexOf('@') === -1) {
                        this.showPasswordField();
                        return;
                    }
                    dataLayer.push({
                        event: 'SignupLoginErrorView',
                        errorName: 'Not Exist User',
                    });
                    this.dialogOpen = true;
                    this.dialogOpen = await this.$dialog.createAccount({ id: this.inputAccountId });
                    return;
                } else if (errorCode === 20212) {
                    dataLayer.push({
                        event: 'SignupLoginErrorView',
                        errorName: 'Password Needed on Log In',
                    });
                    this.dialogOpen = true;
                    this.dialogOpen = await this.$dialog.setPassword({ id: this.inputAccountId });
                    return;
                } else if (errorCode === 20048 && (this.inputAccountId.match(/@/g) || []).length > 1) {
                    this.showInvalidEmailError = true;
                    return;
                }
                this.handleLoginFailure(err);
            }
        },
        showPasswordField() {
            this.showPassword = true;
            setTimeout(() => {
                const elInputPass = document.querySelector('input#password');
                if (elInputPass) elInputPass.focus();
            });
        },
        setCaptchaCode() {
            this.reCaptchaCode = window.grecaptcha.enterprise.getResponse(window.rcWidgetId);
        },
        async loginHandler() {
            this.showInvalidEmailError = false;
            this.inputAccountId = this.inputAccountId.trim(); // remove white space from input account id
            this.$v.$touch();
            this.submitted = true;
            if (!this.$v.$invalid) {
                this.buttonLoading = true; // button spinner behavior
                this.errorCode = 0; // reset error
                this.errorActionMessage = '';
                dataLayer.push({ event: 'formSubmit', formName: this.$route.name });

                try {
                    await this.login({ id: this.inputAccountId, password: this.inputPassword });
                } catch ({ errorCode, message, email }) {
                    // if there is a pending redirection made from axios interceptors
                    if (this.$router.history.pending) return;
                    this.isShowLoading = false;
                    this.handleLoginFailure({ errorCode, message, email });
                }
                this.buttonLoading = false;
            }
        },
        async openModal({ code, email }) {
            this.$store.commit('login/setId', this.errorObj ? this.errorObj.email : this.inputAccountId);
            const result = await this.$dialog.confirm({
                title: `error.modal.${code}.title`,
                content: `error.modal.${code}.desc`,
                confirmLabel: `error.modal.${code}.button`,
                cancelLabel: 'cancel',
            });
            if (result) {
                this.modalContinue(email);
            }
        },
        async openPortalBanModal({ mode, banReason }) {
            await this.$dialog.portalBan({
                inputAccountId: this.inputAccountId,
                inputPassword: this.inputPassword,
                loginType: mode,
                banReason,
            });
        },
        async openMinorLinkingModal(platforms) {
            this.submitted = false;
            const doDelink = await this.$dialog.minorLinking(platforms);
            if (doDelink) {
                this.$globalAccountAPI
                    .delinkMinorConsole()
                    .then(() => {
                        this.$dialog.confirm({
                            title: 'dialog.minor-linking.delink.modal.success.title',
                            content: 'dialog.minor-linking.delink.modal.success.body',
                            cancelLabel: '',
                        });
                        this.$v.$reset();
                        this.inputAccountId = '';
                        this.inputPassword = '';
                        this.$store.commit('login/setId', this.inputAccountId);
                        this.$store.commit('login/setPassword', this.inputPassword);
                    })
                    .catch(() => {
                        this.$dialog.confirm({
                            title: 'dialog.minor-linking.delink.modal.fail.title',
                            content: 'dialog.minor-linking.delink.modal.fail.body',
                            cancelLabel: '',
                        });
                    });
            }
        },
        async openTermsOfServiceModal() {
            await this.$dialog.termsOfService();
        },
        async openTermsOfServiceForMinorModal({ parentEmail }) {
            await this.$dialog.termsOfServiceForMinor({ parentEmail });
        },
        modalContinue(email) {
            if (this.modalSubmitMode === 'request-unprotect-s') {
                this.$router.push({ name: 'smode' });
            } else if (this.modalSubmitMode === 'blocked-user-suspicious-ip') {
                this.$router.push({ name: 'self-reset-password' });
            } else {
                this.$accountAPI
                    .post(`/no-auth/protect/${this.modalSubmitMode}`, {
                        id: email,
                    })
                    .then(() => {
                        this.$router.push({ name: 'nmode' });
                    })
                    .catch(({ errorCode }) => {
                        this.errorCode = errorCode;
                    })
                    .then(() => {
                        this.buttonLoading = false;
                    });
            }
        },
        handleLoginFailure({ errorCode, message, mode = 'normal', email = this.inputAccountId }) {
            // convert BLOCKED_USER_SUSPICIOUS_IP to BLOCKED_USER_PORTAL_BAN for all TPA - OCPFE-456
            if (errorCode === 20102 && mode === 'social') {
                errorCode = 20100; // BLOCKED_USER_PORTAL_BAN
            }
            dataLayer.push({
                event: 'SignupLoginErrorView',
                errorName: this.$t(`error.form.${errorCode}`, 'en'),
            });
            switch (errorCode) {
                // Only for a few cases we show a modal error dialog
                case 20036: // PROTECTED_USER_N_MODE
                case 20037: // PROTECTED_USER_S_MODE
                case 20102:
                    this.openModal({ code: errorCode, email });
                    this.modalErrorCode = errorCode;
                    return;
                case 20027: // TRUST_DEVICE_REQUIRED
                case 20111: // AUTHENTICATOR_NOT_VERIFIED
                case 20204: // SMS_NOT_VERIFIED
                    {
                        const authType = errorCode === 20027 ? 'Email' : errorCode === 20111 ? '2FA' : 'SMS';
                        dataLayer.push({
                            event: '2FA Required on Login',
                            '2FAType': authType.replace('2FA', 'App'),
                        });
                        this.$store.commit('login/setIsTPA', mode === 'social');
                        this.$store.commit('login/setId', email);
                        this.$store.commit('login/setPassword', this.inputPassword);
                        this.$router.push({
                            name: `login-verify-${authType.toLowerCase()}`,
                        });
                    }
                    return;
                case 20100: // BLOCKED_USER_PORTAL_BAN
                    this.openPortalBanModal({ mode, banReason: message });
                    return;
                case 20251: // CONSOLE_LINKING_HAS_MINOR
                    this.openMinorLinkingModal(message);
                    return;
                case 20072: // NEED_CAPTCHA
                    this.showCaptcha = true;
                    break;
                case 20042: // NOT_EXIST_MEMBERSHIP_USER
                    // eslint-disable-next-line no-case-declarations
                    let region = message ? JSON.parse(message).userMembershipRegion.toLowerCase() : '';
                    if (region && this.isLauncherMode()) {
                        errorCode = 20074;
                    } else if (region) {
                        region = region === 'west' ? 'global' : region;
                        dataLayer.push({
                            event: 'Viewed Wrong Region Alert',
                            detectedRegion: region,
                        });
                        this.errorActionMessage = this.$t('error.go-to-region', { region });
                        this.errorAction = this.switchRegion;
                        this.switchToRegion = region;
                    }
                    break;
                case 20029: // UNVERIFIED
                    this.$store.commit('setSignupParams', {
                        id: email,
                        password: this.inputPassword,
                    });
                    this.$router.push({
                        name: 'login-verify-email',
                        params: {
                            fromLogin: true,
                        },
                    });
                    break;
                case 20235: // NOT_UNDERAGE for child addition
                    errorCode = 'ALREADY_EXIST_ACCOUNT_OR_INVALID_FOR_CHILD';
                    break;
                case 20255: // RE_CONSENT_REQUIRED
                    this.openTermsOfServiceModal();
                    return;
                case 20256: // PARENT_RE_CONSENT_REQUIRED
                    this.openTermsOfServiceForMinorModal({ parentEmail: message });
                    return;
            }

            // Otherwise just show it in the top form error
            this.errorCode = errorCode;
        },
        switchRegion() {
            if (!this.switchToRegion) return;
            dataLayer.push({
                event: 'SignupLoginErrorClick',
                errorName: 'Wrong Region',
                errorClick: this.$t('error.go-to-region', { region: this.switchToRegion }),
            });
            const url = domains[process.env.VUE_APP_ENV][this.switchToRegion].url;
            window.open(`${url}/account/login`, '_self', 'noreferrer');
        },
        trackTroubleLogging() {
            dataLayer.push({
                event: 'accountRecoverySelfService_OtherLinkClick',
                'AR Link Name': this.$t('login.help.trouble-logging-in', 'en'),
            });
        },
    },
};
</script>

<style lang="less">
li.trouble-logging-in {
    font-size: 14px;
    font-weight: bold;
    text-decoration: underline;
    .icon-help {
        height: 9px;
        margin-right: 4px;
    }
}
.error-form-action {
    &.clickable {
        cursor: pointer;
    }
    text-decoration: underline;
    text-transform: uppercase;
}
.hide-social {
    .contents {
        max-width: 400px;
    }
    .column {
        min-width: 320px;
        max-width: 400px;

        &.pull-left,
        &.pull-right {
            max-width: 400px;
        }
    }

    .pull-left {
        float: none !important;
        margin: 0 auto;
    }
}

.parental-flow {
    .contents,
    .pull-left,
    .pull-right {
        max-width: 400px !important;
        width: 100%;
    }

    .pull-left {
        .recaptcha-attribution {
            display: none;
        }
    }
    .pull-right {
        .divider {
            display: flex;
        }
        .recaptcha-attribution {
            display: block;
        }
    }
}

.pull-left {
    .recaptcha-attribution {
        display: block;
    }
}
.pull-right {
    .divider {
        display: none;
        flex-direction: row;
        color: white;
        &:before,
        &:after {
            content: '';
            flex: 1 1;
            border-bottom: 1px solid #4c4c4c;
            margin: auto;
        }

        &:before {
            margin-right: 10px;
        }
        &:after {
            margin-left: 10px;
        }
    }
    .recaptcha-attribution {
        display: none;
    }
}

@media (max-width: 740px) {
    .pull-left {
        .recaptcha-attribution {
            display: none;
        }
    }
    .pull-right {
        .divider {
            display: flex;
        }
        .recaptcha-attribution {
            display: block;
        }
    }
}
.launcher-page {
    ::-webkit-scrollbar {
        width: 5px;
        height: 5px;
    }
    ::-webkit-scrollbar-button {
        display: none;
    }
    ::-webkit-scrollbar-track {
        width: 5px;
        height: 5px;
    }
    ::-webkit-scrollbar-thumb {
        width: 5px;
        height: 5px;
        border-radius: 5px;
        background-color: rgba(255, 255, 255, 0.2);
    }
    ::-webkit-scrollbar-thumb:hover {
        background-color: rgba(255, 255, 255, 0.5);
    }
    ::-webkit-scrollbar-corner {
        background-color: transparent;
    }
}
.modal-dialog {
    .modal-body {
        text-align: center;
    }
}
</style>
